import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Lock, Clock as ClockIcon, TrendingUp, Percent, Eye } from "lucide-react";

function fmtMoney(n) {
  const num = Number(n) || 0;
  const sign = num < 0 ? "-" : "";
  return `${sign}$${Math.abs(num).toFixed(2)}`;
}

function MasterJournalDialog({ open, onOpenChange, masterId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !masterId) return;
    setLoading(true);
    api.get(`/masters/${masterId}/journal`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  }, [open, masterId]);

  const stats = data?.stats;
  const winRate = stats && stats.trades_count > 0 ? Math.round((stats.wins / stats.trades_count) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="master-journal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Star size={16} className="text-amber-500 fill-amber-400" />
            {data?.master?.name || data?.master?.email || "Master Trader"}
          </DialogTitle>
          <DialogDescription className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">
            LP #{data?.master?.loyal_primus_client_id || "—"}
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && data && (
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Trade P&L</div>
                <div className={`mt-1 font-display font-bold text-lg ${stats.trade_pnl >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtMoney(stats.trade_pnl)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Win rate</div>
                <div className="mt-1 font-display font-bold text-lg">{winRate}%</div>
                <div className="text-[10px] font-mono-plex text-slate-500">{stats.wins}W / {stats.losses}L</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Trades</div>
                <div className="mt-1 font-display font-bold text-lg">{stats.trades_count}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">Deposits</div>
                <div className="mt-1 font-display font-bold text-lg text-[#00b4ff]">{fmtMoney(stats.deposits)}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 mb-2">Entries ({data.entries.length})</div>
              {data.entries.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-slate-500 text-sm text-center">Master ni belum ada entry.</div>
              ) : (
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-96 overflow-y-auto">
                  {data.entries.map((e) => {
                    const isProfit = e.kind === "trade" && e.amount >= 0;
                    const isLoss = e.kind === "trade" && e.amount < 0;
                    const isDeposit = e.kind === "deposit";
                    const label = e.kind === "trade" ? (isProfit ? "PROFIT" : "LOSS") : e.kind.toUpperCase();
                    let color = "text-slate-700";
                    if (isProfit) color = "text-emerald-700";
                    else if (isLoss) color = "text-rose-700";
                    else if (isDeposit) color = "text-[#00b4ff]";
                    return (
                      <div key={e.id} className="px-4 py-3 flex items-center justify-between gap-3 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-500">{e.date}</span>
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-400">·</span>
                            <span className="text-[9px] font-mono-plex tracking-widest uppercase text-slate-600">{label}</span>
                          </div>
                          {e.note && <div className="mt-0.5 text-xs text-slate-600 truncate">{e.note}</div>}
                        </div>
                        <div className={`font-mono-plex font-semibold shrink-0 ${color}`}>
                          {e.kind === "trade" ? fmtMoney(e.amount) : `$${Number(e.amount).toFixed(2)}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MasterTraders() {
  const [masters, setMasters] = useState([]);
  const [requesting, setRequesting] = useState({});
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/masters"); setMasters(data); }
    catch (err) { logDev(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requestAccess = async (m) => {
    setRequesting((p) => ({ ...p, [m.id]: true }));
    try {
      await api.post(`/masters/${m.id}/request-view`);
      toast.success("Request sent. Tunggu admin approve.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Request failed");
    } finally {
      setRequesting((p) => ({ ...p, [m.id]: false }));
    }
  };

  if (masters.length === 0) {
    return <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-slate-500 text-sm">Belum ada Master Trader. Admin akan upgrade top performers.</div>;
  }

  return (
    <div>
      <div className="mb-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50 to-white p-5">
        <div className="flex items-center gap-2 text-[10px] font-mono-plex tracking-[0.28em] uppercase text-amber-700">
          <Star size={12} className="fill-amber-400" /> How this works
        </div>
        <div className="mt-2 text-sm text-slate-700 leading-relaxed">
          Master Traders adalah members yang dah proven consistency. Anda boleh request untuk tengok Trade Journal mereka untuk belajar entry, exit dan risk management. Admin akan review setiap request.
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {masters.map((m, i) => {
          const status = m.request_status;
          const canView = m.has_access;
          return (
            <div key={m.id} className="stagger-item rounded-2xl border border-slate-200 bg-white p-5 relative"
                 style={{ animationDelay: `${i * 40}ms` }}
                 data-testid={`master-${m.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center text-white shadow-lg shrink-0">
                  <Star size={20} className="fill-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold tracking-tight truncate">
                    {m.name || m.email.split("@")[0]}
                  </div>
                  <div className="text-xs font-mono-plex text-slate-500 truncate">LP #{m.loyal_primus_client_id || "—"}</div>
                </div>
              </div>

              <div className="mt-4">
                {canView ? (
                  <button
                    data-testid={`view-master-${m.id}`}
                    onClick={() => setSelected(m.id)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 rounded-full text-sm font-semibold"
                    style={{ transition: "background-color 0.2s ease" }}
                  >
                    <Eye size={14} /> View Trade Journal
                  </button>
                ) : status === "pending" ? (
                  <div data-testid={`master-pending-${m.id}`} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-mono-plex tracking-widest uppercase px-4 py-2.5">
                    <ClockIcon size={12} /> Waiting admin
                  </div>
                ) : status === "rejected" ? (
                  <>
                    <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2 mb-2">
                      Rejected{m.reject_reason ? `: ${m.reject_reason}` : ""}
                    </div>
                    <button
                      data-testid={`master-request-again-${m.id}`}
                      onClick={() => requestAccess(m)}
                      disabled={requesting[m.id]}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white px-4 py-2.5 rounded-full text-sm font-semibold"
                      style={{ transition: "background-color 0.2s ease" }}
                    >
                      {requesting[m.id] ? "Sending…" : "Try again"}
                    </button>
                  </>
                ) : (
                  <button
                    data-testid={`master-request-${m.id}`}
                    onClick={() => requestAccess(m)}
                    disabled={requesting[m.id]}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-60 text-white px-4 py-2.5 rounded-full text-sm font-semibold"
                    style={{ transition: "background-color 0.2s ease" }}
                  >
                    <Lock size={12} /> {requesting[m.id] ? "Sending…" : "Request Access"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <MasterJournalDialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }} masterId={selected} />
    </div>
  );
}
